import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../services/i18n/language';
import { CompanyAssignee, CompanyService } from '../../../services/api/company';

export interface AssigneeChange {
  id: number | null;
  name: string | null;
}

/**
 * Ответственный сотрудник: менеджер запроса или оператор сделки. Если права на назначение нет, показывает имя
 * (или «Не назначен»), если есть, выпадающий список сотрудников компании с нужной ролью.
 */
@Component({
  selector: 'app-assignee-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assignee-select.html',
  styleUrl: './assignee-select.scss'
})
export class AssigneeSelectComponent implements OnInit, OnChanges, OnDestroy {
  /** manager: менеджер запроса, operator: оператор сделки. */
  @Input() kind: 'manager' | 'forwarderManager' | 'operator' = 'operator';
  /** Id запроса (kind = manager) или сделки (kind = operator). */
  @Input() entityId!: number;
  @Input() currentId: number | null | undefined = null;
  @Input() currentName: string | null | undefined = null;
  @Input() canEdit = false;
  @Output() assigned = new EventEmitter<AssigneeChange>();

  options: CompanyAssignee[] = [];
  selected: number | null = null;
  saving = false;
  error: string | null = null;
  saved = false;

  private subscriptions = new Subscription();

  constructor(
    private companyService: CompanyService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.loadOptions();
  }

  ngOnChanges(): void {
    this.selected = this.currentId ?? null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  get label(): string {
    return this.kind !== 'operator'
      ? this.t('company.assign.managerLabel', 'Ответственный менеджер')
      : this.t('company.assign.operatorLabel', 'Ответственный оператор');
  }

  private loadOptions(): void {
    if (!this.canEdit) {
      return;
    }
    const request = this.kind !== 'operator' ? this.companyService.getManagers() : this.companyService.getOperators();
    request.subscribe({
      next: options => {
        this.options = options;
        this.cdr.markForCheck();
      },
      error: () => {
        this.options = [];
        this.cdr.markForCheck();
      }
    });
  }

  change(userId: number | null): void {
    if (userId === (this.currentId ?? null) || this.saving) {
      return;
    }
    this.saving = true;
    this.error = null;
    this.saved = false;
    const call = this.kind === 'manager'
      ? this.companyService.assignRequestManager(this.entityId, userId)
      : this.kind === 'forwarderManager'
        ? this.companyService.assignForwarderManager(this.entityId, userId)
        : this.companyService.assignDealOperator(this.entityId, userId);
    call.subscribe({
      next: () => {
        this.saving = false;
        this.saved = true;
        const name = this.options.find(o => o.id === userId)?.fullName ?? null;
        this.assigned.emit({ id: userId, name });
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.selected = this.currentId ?? null;
        this.error = err.error?.message || this.t('company.assign.error', 'Не удалось назначить сотрудника');
        this.cdr.markForCheck();
      }
    });
  }
}
